import { ChangeDetectorRef, inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, PartialObserver, Subject, Subscription } from 'rxjs';

@Injectable()
export abstract class BaseComponent implements OnDestroy {
  protected readonly destroyed = new Subject<void>();

  protected readonly subscriptions = new Subscription();

  private readonly baseComponentChangeDetectorRef = inject(ChangeDetectorRef);

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
    this.subscriptions.unsubscribe();
  }

  /**
   * @deprecated Use `takeUntilDestroyed` or `rxResource` or `rxCommand` instead. This method will be removed in a future version.
   */
  protected subscribe<T>(observable: Observable<T>, next?: (value: T) => void): Subscription;
  protected subscribe<T>(observable: Observable<T>, observer: PartialObserver<T>): Subscription;
  protected subscribe<T>(observable: Observable<T>, observerOrNext: any): Subscription {
    const observer: PartialObserver<T> = observerOrNext instanceof Function ? { next: observerOrNext } : (observerOrNext ?? {});
    const onError = observer.error;
    const onComplete = observer.complete;
    // Angular's zone-based change detection no longer picks up plain field mutations made from
    // inside RxJS subscribe callbacks (see Angular 22's notification-based scheduler). Without this,
    // views only refresh once some unrelated Angular-tracked event (e.g. a click) happens to run.
    const subscription = observable.subscribe({
      next: (value: T) => {
        observer.next?.(value);
        this.baseComponentChangeDetectorRef.markForCheck();
      },
      error: onError
        ? (err: unknown) => {
            onError(err);
            this.baseComponentChangeDetectorRef.markForCheck();
          }
        : undefined,
      complete: onComplete
        ? () => {
            onComplete();
            this.baseComponentChangeDetectorRef.markForCheck();
          }
        : undefined,
    });
    this.subscriptions.add(subscription);
    return subscription;
  }
}
